/**
 * @module experience/order-summary
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// tslint:disable:max-file-line-count
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// core libraries
import { Injectable, EventEmitter } from '@angular/core';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import {
  OrderSummaryInterface,
  Product,
  ShipToName,
  Eligibility,
  LineDetails,
  ShipmentEligibilityRequest,
  ShipmentEligibilityResponse,
} from '@support/api/proxies/responses/api-2814-new.interface';
import { PendingOrderInformationInterface } from '@support/api/proxies/responses/api-2814-getOrderDetails.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Constants
import {
  ORDER_SUMMARY_CONSTANT,
  ORDER_TYPE_CONSTANT,
} from '@experience/order-summary/order-summary.constants';
import { FIELD_VALIDATIONS_CONSTANTS } from '@support/shared/services/common-api/common-api.constants';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { Api416Request } from '@support/api/proxies/requests/api-416.interface';
import { Api416Response } from '@support/api/proxies/responses/api-416.interface';
import { Document } from '@support/api/proxies/responses/api-497.interface';
import { SendEmailResponse } from '@support/api/proxies/responses/api-515.interface';
import {
  SelectedItems,
  RefundSelectedItems,
  Selection,
  DocumentId,
  Eligibilities,
  OrderLine,
  CheckboxEligible,
} from '@experience/order-summary/interfaces/order-summary-new.interface';
import { SpecialInstructionEvent } from '@experience/landing/common/modules/special-instructions/interfaces/special-instructions.interface';
import {
  OrderSearchResults,
  OrderSearchResponse,
} from '@support/api/proxies/responses/api-2813.interface';
import { CancelOrderData } from '@support/api/proxies/requests/api-2815.interface';
import { CancelOrderDetails } from '@support/api/proxies/responses/api-2815.interface';
import { Reasons } from '@support/api/proxies/responses/api-326.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { Wove } from 'aspect.js';
import { SendEmailRequest } from '@support/api/proxies/requests/api-515.interface';
import { UpdateMileStoneResponse } from '@support/api/proxies/responses/api-2818.interface';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { Router } from '@angular/router';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { VIEW_STATEMENT } from '@experience/shop/order-confirmation/constants/order-confirmation.constants';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
@Wove({ moduleName: ORDER_TYPE_CONSTANT.ORDER_MODULE })
@Injectable(
  { providedIn: 'root' }
)
export class OrderSummaryService {
  /**
   * Stores selected items for return/exchange
   */
  private selectedItems: SelectedItems;
  /**
   * Stores selected items for refund
   */
  private refundSelectedItems: RefundSelectedItems[];
  /**
   * Stores the selected filters
   */
  public selectedFilters: SpecialInstructionEvent[];
  /**
   * Emitter to emit the selected filters
   */
  public filterCriteria: EventEmitter<SpecialInstructionEvent[]>;
  /**
   * Subject to update the new order in focus
   */
  public orderIdUpdatedSubject: Subject<boolean>;
  /**
   * Subject to update the selection of checkbox
   */
  public checkBoxSelectionSubject: Subject<object>;
  /**
   * Observable to detect when there is a change in line in focus
   */
  public checkBoxSelection$: Observable<object>;
  /**
   * Observable to detect when there is a change in line in focus
   */
  public orderIdUpdated$: Observable<boolean>;
  /**
   * native window object
   */
  public windowContainer: Window;

  public isSimKitRefund: boolean;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private stateStoreUtilService: StateStoreUtilsService,
    private objectService: ObjectService,
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private commonUtils: CommonUtilService,
    private router: Router,
  ) {
    this.selectedItems = <SelectedItems>new Object();
    this.selectedItems.accessory = [];
    this.selectedItems.devices = [];
    this.selectedItems.directToAccountData = [];
    this.selectedItems.ePinData = [];
    this.selectedItems.line = [];
    this.refundSelectedItems = [];
    this.filterCriteria = new EventEmitter<SpecialInstructionEvent[]>();
    this.selectedFilters = [];
    this.orderIdUpdatedSubject = new Subject();
    this.orderIdUpdated$ = this.orderIdUpdatedSubject.asObservable();
    this.checkBoxSelectionSubject = new Subject();
    this.checkBoxSelection$ = this.checkBoxSelectionSubject.asObservable();
  }

  /**
   * Calls API+2814 to get pending order details
   * @name getOrderSummary
   * @methodOf myTmoApp.orderSummary.service:OrderSummaryService
   * @description To fetch all the details of order
   * @returns {Observable} Promise of the order details
   */
  public getOrderSummary(
    selectedFilters?: SpecialInstructionEvent[],
    isCps?: boolean,
    orderId?: string
  ): Observable<OrderSummaryInterface> {
    let pathParams = {
      orderId: '',
    };
    const accountId = this.stateStoreUtilService.getAccountId();
    pathParams = {
      orderId: orderId ? orderId : this.stateStoreUtilService.getCustomerOrderId(),
    };

    const payload = {
      accountId: accountId || '',
      sendOrderDetailsOnly: false,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2814_getOrderDetails, payload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const orderDetails = response.body;
          const copyOfOrderDetails = JSON.parse(JSON.stringify(orderDetails));
          this.stateStoreUtilService.setOrderSummaryData(copyOfOrderDetails);
          return orderDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Calls API 2814 to get pending order details
   * @name getPendingOrderSummary
   * @methodOf myTmoApp.orderSummary.service:OrderSummaryService
   * @description To fetch all the details of order
   * @returns {Observable} Promise of the order details
   */
  public getPendingOrderSummary(
    selectedFilters?: SpecialInstructionEvent[],
    orderId?: string
  ): Observable<PendingOrderInformationInterface> {
    let pathParams;
    let requestPayload;
    pathParams = {
      orderId: orderId ? orderId : '',
    };
    requestPayload = {
      accountId: this.stateStoreUtilService.getAccountId() || '',
      sendOrderDetailsOnly: false,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2814_getOrderDetails, requestPayload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<any>) => {
          const orderDetails = response.body;
          this.stateStoreUtilService.setOrderSummaryData(orderDetails);
          return orderDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param toLock: Boolean value to determine whether to Lock or Unlock the order
   * @returns Observable<Api416Response>
   */
  public lockUnlockOrder(
    toLock?: boolean,
    actionCode?: string,
    isCps?: boolean,
    orderId?: string
  ): Observable<Api416Response> {
    let pathParams = {
      orderId: '',
    };
    let requestPayload: Api416Request = {
      actionCode: '',
    };
    if (isCps) {
      pathParams = {
        orderId: orderId,
      };
      requestPayload = {
        actionCode: actionCode,
      };
    } else {
      pathParams = {
        orderId: this.stateStoreUtilService.getCustomerOrderId(),
      };
      requestPayload = {
        actionCode: toLock ? actionCode : ORDER_SUMMARY_CONSTANT.UNLOCK,
      };
    }

    return this.apiGatewayService
      .postRequest(API_MAP.API_416_lockOrder, requestPayload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Api416Response>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param shipToDetails: containing shipTo details
   * @returns FormGroup
   */
  public buildShippingNameForm(shipToDetails: ShipToName): UntypedFormGroup {
    let shippingNameFormGrp: UntypedFormGroup;
    const firstName = this.objectService.get(shipToDetails, 'firstName');
    const middleName = this.objectService.get(shipToDetails, 'middleName');
    const lastName = this.objectService.get(shipToDetails, 'lastName');
    shippingNameFormGrp = this.formBuilder.group({
      firstName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.FIRST_NAME,
        firstName ? firstName : '',
        true
      ),
      middleName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.MIDDLE_INITIAL,
        middleName ? middleName : '',
        false
      ),
      lastName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.LAST_NAME,
        lastName ? lastName : '',
        true
      ),
    });
    return shippingNameFormGrp;
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param documentData: document
   * @returns documentUrl: string
   */
  public getDocument(documentId: string, orderId?: string, docType?: string): Observable<string> {
    // const isWebChannel = (SETTINGS.CHANNEL.toLocaleLowerCase() === VIEW_STATEMENT.WEB);
    let request = {};
    request = {
      orderAttachment: {
        // docReturnType: (isWebChannel) ? VIEW_STATEMENT.CONTENT : VIEW_STATEMENT.URL,
        docReturnType: VIEW_STATEMENT.CONTENT,
        orderId: orderId,
        documentId: documentId,
        documentType:
          docType.toLocaleUpperCase() === VIEW_STATEMENT.RECEIPT
            ? VIEW_STATEMENT.RECEIPT
            : docType === VIEW_STATEMENT.SHIPPING_LABEL
              ? undefined
              : VIEW_STATEMENT.TRANSACTION_CONFIRMATION,
      },
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_497_retrieveDocuments, request, null, null, null)
      .pipe(
        map((response: HttpResponse<Document>) => {
          // return this.getDocumentContent(response.body, isWebChannel);
          return this.getDocumentContent(response.body);
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  // TODO It will be updated later in US308461
  /**
   * Public method to lock or Unlock the order while canceling it
   * @param emailId: string
   * @param orderInfo: OrderInformationInterface
   * @returns  Observable of type SendEmailResponse
   */
  public sendEmail(
    emailId: string,
    orderInfo: OrderSummaryInterface
  ): Observable<SendEmailResponse> {
    const lineOfServiceId = this.stateStoreUtilService.getLineId() || '';
    const customerId = this.stateStoreUtilService.getRepActiveCustomerId() || '';
    const customerDetails = this.stateStoreUtilService.getCustomerInfo(customerId);
    const accountId = this.stateStoreUtilService.getAccountId() || '';
    const msisdn = this.stateStoreUtilService.getMsisdn() || '';
    const request: SendEmailRequest = {
      event: {
        eventType: 'ReceiptResendRequested',
        eventId: '',
        eventTime: '',
        customerId: customerId,
        accountId: accountId,
        lineOfServiceId: lineOfServiceId,
        orderId: orderInfo.orderId,
        msisdn: msisdn,
        customer: {
          firstName: this.objectService.get(customerDetails, 'firstName'),
          lastName: this.objectService.get(customerDetails, 'lastName'),
          orderDate: orderInfo.orderDate.value,
          preferredLanguage: 'en',
          emailAddress: emailId,
        },
      },
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_515_sendEmail, request, null, null, null, null, false)
      .pipe(
        map((response: HttpResponse<SendEmailResponse>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Public method to clear selected items
   * @returns void
   */
  public clearSelectedProductDetails(): void {
    this.selectedItems = <SelectedItems>new Object();
    this.selectedItems.accessory = [];
    this.selectedItems.devices = [];
    this.selectedItems.directToAccountData = [];
    this.selectedItems.ePinData = [];
    this.selectedItems.type = null;
    this.selectedItems.line = [];
  }

  /**
   * Public method to lock or Unlock the order while canceling it
   * @param documentData: document
   * @returns documentUrl: string
   */
  // private getDocumentContent(documentData: Document, isWebChannel: boolean): string {
  private getDocumentContent(documentData: Document): string {
    // if (isWebChannel) {
    return this.objectService.get(documentData, 'documentDetails.content.embeddedData');
    // }else {
    //   return this.objectService.get(documentData, 'documentDetails.documentURL');
    // }
  }

  /**
   * Method for calling API 2813  searchOrders.
   * @param lookUpParams : parameters to be passed in the get request
   * Onject for Order Id Array Response
   */
  public getPendingOrderDetails(lookUpParams: {
    [name: string]: string;
  }): Observable<OrderSearchResults[]> {
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .postRequest(API_MAP.API_2813_getSearchOrders, lookUpParams, headers)
      .pipe(
        map((response: HttpResponse<OrderSearchResponse>) => {
          return response.body.orderSearchResults;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Get Reason drop down
   * @param type
   * @returns Observable
   */
  public getPostVoidReason(type: { [key: string]: string }): Observable<Reasons> {
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .getRequest(ASSISTED_API_MAP.API_326_getAdjustmentReasons, headers, type)
      .pipe(
        map((response: HttpResponse<Reasons>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * API call 2818_updateMileStone to for manual activation
   * @param orderData : parameters to be passed in the request
   * @returns Observable
   */
  public updateMileStone(orderData: OrderSummaryInterface): Observable<UpdateMileStoneResponse> {
    const payload = {
      orderLines: [],
    };
    const lines: LineDetails[] = this.objectService.get(orderData, 'orderItems.lines');
    lines.forEach((line: LineDetails) => {
      payload.orderLines.push({
        orderLineId: this.objectService.get(line, 'lineId'),
        imei: this.objectService.get(line, 'devices.0.imei'),
        imsi: this.objectService.get(line, 'plans.0.imsi'),
      });
    });
    return this.apiGatewayService
      .postRequest(API_MAP.API_2818_updateMileStone, payload, null, null, {
        orderId: this.objectService.get(orderData, 'orderId'),
      })
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method to call pending order for the given order id
   * @param {string} orderId
   * @param {string} orderStatus
   * @param {string} reasonCode
   * @returns {boolean}
   */
  public cancelPostVoidOrder(
    orderId: string,
    orderStatus: string,
    reasonCode: string,
    isPostVoid: boolean
  ): Observable<CancelOrderDetails> {
    const headers = new HttpHeaders();
    const payload: CancelOrderData = {
      orderId: orderId,
      orderStatus: orderStatus,
      reasonCode: reasonCode,
      isPostVoid: isPostVoid,
      posId: this.stateStoreUtilService.getMacAddress(),
    };
    const pathParams = {
      orderId: orderId,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2815_postCancelOrder, payload, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<CancelOrderDetails>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Updates the value of the selected item
   * @param type; stores the value of the type of ePin
   * @param checked: boolean value to check whether the chechbox is checked
   * @param product; stores the value of the product
   * @param productType: stores the type of product i.e. accessory or device
   * @returns void
   */
  public updateSelectedItem(
    type: string,
    checked: boolean,
    product?: Product,
    productType?: string,
    lineId?: string
  ): void {
    if (this.objectService.get(this.selectedItems, 'type') !== type) {
      this.clearSelectedProductDetails();
    }
    this.selectedItems.type = type;
    if (this.commonUtils.isNotEmpty(lineId)) {
      const selectedLine = this.selectedItems.line.find((line: OrderLine) => {
        return line.id === lineId;
      });
      let index = -1;
      if (this.commonUtils.isNotEmpty(selectedLine)) {
        index = this.selectedItems.line.indexOf(selectedLine);
      }
      this.updateLineItem(checked, index, product, lineId);
    } else {
      const index = this.selectedItems[productType].indexOf(product);
      if (checked && index === -1) {
        this.selectedItems[productType].push(product);
      } else if (!checked && index !== -1) {
        this.selectedItems[productType].splice(index, 1);
      }
    }
    this.selectionUpdate(type, checked);
  }

  /**
   * updates line Items
   * @param checked: boolean value to check whether the chechbox is checked
   * @param product; stores the value of the product
   * @param productType: stores the type of product i.e. accessory or device
   * @returns void
   */
  private updateLineItem(checked: boolean, index: number, product: Product, id: string): void {
    if (checked && index === -1) {
      const line = {
        id: id,
        devices: product,
      };
      this.objectService.get(this.selectedItems, 'line').push(line);
    } else if (!checked && index !== -1) {
      this.objectService.get(this.selectedItems, 'line').splice(index, 1);
    }
  }
  /**
   * To update the value of selection
   * @returns void
   * @param checked
   * @param type
   */
  private selectionUpdate(type: string, checked: boolean): void {
    const selection: Selection = {
      exchangeDisable: false,
      returnDisable: false,
    };
    if (checked && type === ORDER_SUMMARY_CONSTANT.RETURN) {
      selection.exchangeDisable = checked;
    } else if (checked && type === ORDER_SUMMARY_CONSTANT.EXCHANGE) {
      selection.returnDisable = checked;
    } else if (!checked) {
      selection.exchangeDisable = checked;
      selection.returnDisable = checked;
    }
    this.checkBoxSelectionSubject.next(selection);
  }

  /**
   * To update the value of Refund Items
   * @returns void
   * @param checked
   * @param index
   * @param chargeDocuments
   * @param isRefundCancelled
   */

  public updateRefundItems(
    checked: boolean,
    chargeDocuments: DocumentId,
    isRefundCancelled?: boolean,
    isSimKitRefund?: boolean
  ): void {
    this.isSimKitRefund = isSimKitRefund;
    if (checked) {
      this.refundSelectedItems.push(chargeDocuments);
    } else if (isRefundCancelled) {
      this.refundSelectedItems = [];
    } else {
      this.refundSelectedItems.forEach((document: DocumentId, index: number) => {
        if (chargeDocuments.chargeDocumentId === document.chargeDocumentId) {
          this.refundSelectedItems.splice(index, 1);
        }
      });
    }
  }

  /**
   * To get the value of selectedItems
   * @returns SelectedItems
   */
  public getSelectedItems(): SelectedItems {
    return this.selectedItems;
  }

  /**
   * To get the value of selectedItems
   * @returns SelectedItems
   */
  public getRefundSelectedItems(): RefundSelectedItems[] {
    return this.refundSelectedItems;
  }

  /**
   * To set the value of selectedItems
   * @param updatedSelectedItems; updated value of selectedItems
   * @returns void
   */
  public setSelectedItems(
    updatedSelectedItems: SelectedItems,
    type?: string,
    checked?: boolean,
    action?: string
  ): void {
    if (checked || action) {
      this.selectedItems = updatedSelectedItems;
      this.selectedItems.type = type;
    }
    this.selectionUpdate(type, checked);
  }

  /**
   * To check permission
   * @param url, abf
   * @returns boolean
   */
  public getPermissionABFValue(url: string, abf: string): boolean {
    const permission = this.stateStoreUtilService.getPermissionABFValue(url, abf);
    return permission === ORDER_SUMMARY_CONSTANT.ENABLE;
  }

  /**
   * To open the PDF document in new window
   * @param url
   */
  public openWindow(url: string): void {
    const params = `scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no`;
    if (this.stateStoreUtilService.isTapestry()) {
      const openPdfLink = document.createElement('a');
      openPdfLink.href = 'openpdf://url=' + url;
      openPdfLink.click();
    } else {
      window.open(url, '_blank', params);
    }
  }

  /**
   * To check eligibilty of products
   * @param item
   * @param eligibility
   * @returns boolean
   */
  public checkEligibilty(
    items: Product[],
    eligibilityType: string,
    itemType: string
  ): CheckboxEligible {
    const eligibleItem: CheckboxEligible = {
      isCheckboxEligible: false,
      isPermissionModalEnable: false,
      hideCheckBox: true,
    };
    let eligibilityResult: CheckboxEligible;
    const isRemorsePermission = this.getPermissionABFValue(
      this.router.url,
      PERMISSION_CONSTANTS.BYPASS_REMORSE_RETURN_RESTR
    );
    items.forEach((item: Product) => {
      if (item && item.eligibilities) {
        item.eligibilities.forEach((eligibility: Eligibility) => {
          if (eligibility.eligibilityType) {
            if (eligibilityType === eligibility.eligibilityType.toUpperCase()) {
              eligibilityResult = this.checkEnablement(eligibility, isRemorsePermission, itemType);
            }
          }
          if (
            eligibilityResult &&
            eligibilityResult.isCheckboxEligible &&
            eligibilityResult.isPermissionModalEnable
          ) {
            eligibleItem.isCheckboxEligible = true;
            eligibleItem.isPermissionModalEnable = true;
          } else if (eligibilityResult && eligibilityResult.isCheckboxEligible) {
            eligibleItem.isCheckboxEligible = true;
            eligibleItem.isPermissionModalEnable = false;
          }
          if (eligibilityResult && !this.objectService.get(eligibilityResult, 'hideCheckBox')) {
            eligibleItem.hideCheckBox = false;
          } else {
            eligibleItem.isCheckboxEligible = false;
          }
        });
      }
    });
    return eligibleItem;
  }

  /**
   * Check the enablment of checkboxes and padlock
   * @param eligibility
   * @param permission
   * @returns CheckboxEligible
   */
  private checkEnablement(
    eligibility: Eligibility,
    permission: boolean,
    itemType?: string
  ): CheckboxEligible {
    const eligible: CheckboxEligible = {
      isCheckboxEligible: false,
      isPermissionModalEnable: false,
      hideCheckBox: eligibility.hideCheckBox,
    };
    if (eligibility.eligibilityIndicator) {
      eligible.isCheckboxEligible = true;
    } else if (eligibility.overrideIndicator && itemType !== ORDER_SUMMARY_CONSTANT.EPIN) {
      eligible.isCheckboxEligible = true;
      eligible.isPermissionModalEnable = true;
    } else {
      eligible.isCheckboxEligible = false;
    }
    return eligible;
  }

  /**
   * To create map of eligiblities to enable/disbale return and exchange checkboxes and show alerts
   * @param item
   * @returns Eligibilities
   */
  public createEligibilitesData(items: Product[]): Eligibilities {
    const isReturnEligible = [];
    const isExchangeEligible = [];
    const ineligibilityReasons = {};
    const defaultCheckboxEligible = {
      isCheckboxEligible: false,
      isPermissionModalEnable: false,
    };
    const isRemorsePermission = this.getPermissionABFValue(
      this.router.url,
      PERMISSION_CONSTANTS.BYPASS_REMORSE_RETURN_RESTR
    );
    items.forEach((item: Product, index: number) => {
      ineligibilityReasons[index] = [];
      isReturnEligible[index] = defaultCheckboxEligible;
      isExchangeEligible[index] = defaultCheckboxEligible;
      if (item && this.commonUtils.isNotEmpty(item.eligibilities)) {
        item.eligibilities.forEach((eligibility: Eligibility) => {
          if (eligibility.eligibilityType) {
            if (eligibility.eligibilityType.toUpperCase() === ORDER_TYPE_CONSTANT.RETURNORDERTYPE) {
              isReturnEligible[index] = this.checkEnablement(eligibility, isRemorsePermission);
            }
            if (eligibility.eligibilityType.toUpperCase() === ORDER_TYPE_CONSTANT.EXCHANGE) {
              isExchangeEligible[index] = this.checkEnablement(eligibility, isRemorsePermission);
            }
            if (
              (!this.commonUtils.isNotEmpty(ineligibilityReasons[index]) ||
                eligibility.eligibilityType.toUpperCase() ===
                  ORDER_TYPE_CONSTANT.RETURNORDERTYPE) &&
              this.commonUtils.isNotEmpty(eligibility.ineligibileReasons)
            ) {
              ineligibilityReasons[index] = eligibility.ineligibileReasons;
            }
          }
        });
      }
    });
    return {
      isReturnEligible: isReturnEligible,
      isExchangeEligible: isExchangeEligible,
      ineligibilityReasons: ineligibilityReasons,
    };
  }

  public checkItemsEligibilityCheck(
    payload: ShipmentEligibilityRequest
  ): Observable<ShipmentEligibilityResponse> {
    return this.apiGatewayService
      .postRequest(API_MAP.API_2826_postShippingEligiblity, payload, null, null, null)
      .pipe(
        map((response: HttpResponse<ShipmentEligibilityResponse>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
}
